import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { RotateCcw, CheckCircle, XCircle, Download, Eraser, PenTool, Ruler } from 'lucide-react';
import { Resource, UIComponent } from '../../types';

interface DrawingBoardComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface Point {
  x: number;
  y: number;
}

interface DrawingPath {
  id: string;
  points: Point[];
  color: string;
  width: number;
  tool: string;
}

interface Template {
  id: string;
  name: string;
  type: 'line' | 'shape' | 'pattern' | 'grid';
  data: any;
}

export const DrawingBoardComponent: React.FC<DrawingBoardComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<DrawingPath | null>(null);
  const [paths, setPaths] = useState<DrawingPath[]>([]);
  const [selectedTool, setSelectedTool] = useState<string>('pen');
  const [selectedColor, setSelectedColor] = useState<string>('#000000');
  const [lineWidth, setLineWidth] = useState<number>(2);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');

  // Get configuration from resource
  const gridSize = resource.gridSize || '10x10';
  const [rows, cols] = gridSize.split('x').map(Number);
  
  // Responsive canvas sizing
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Calculate responsive canvas size based on container width
  const { canvasWidth, canvasHeight } = useMemo(() => {
    if (containerWidth === 0) {
      return { canvasWidth: cols * 40, canvasHeight: rows * 40, cellSize: 40 };
    }
    
    // Calculate available width (subtract padding and tool panel)
    const availableWidth = Math.max(containerWidth - 40, 200); // 40px padding
    const maxCellSize = Math.min(availableWidth / cols, 50); // Max 50px per cell
    const minCellSize = 20; // Min 20px per cell
    
    const cellSize = Math.max(minCellSize, Math.min(maxCellSize, 50));
    const canvasWidth = cols * cellSize;
    const canvasHeight = rows * cellSize;
    
    return { canvasWidth, canvasHeight, cellSize };
  }, [containerWidth, cols, rows]);
  
  // Get settings from resource
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;
  const question = resource.question || 'Tracez selon les consignes données';
  
  // Get tools and templates from resource
  const availableTools: string[] = resource.tools || ['crayon', 'gomme', 'règle', 'formes'];
  const availableTemplates: Template[] = resource.templates || [];

  const colors = [
    '#000000', '#FF0000', '#00FF00', '#0000FF', 
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'
  ];

  const lineWidths = [1, 2, 4, 6, 8];

  // Track container width for responsive sizing
  useEffect(() => {
    if (containerRef) {
      const updateWidth = () => {
        if (containerRef) {
          setContainerWidth(containerRef.offsetWidth);
        }
      };
      
      updateWidth();
      window.addEventListener('resize', updateWidth);
      
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [containerRef]);

  const templates: Template[] = [
    {
      id: 'horizontal_lines',
      name: 'Lignes horizontales',
      type: 'line',
      data: { direction: 'horizontal', spacing: 2 }
    },
    {
      id: 'vertical_lines',
      name: 'Lignes verticales',
      type: 'line',
      data: { direction: 'vertical', spacing: 2 }
    },
    {
      id: 'diagonal_lines',
      name: 'Lignes diagonales',
      type: 'line',
      data: { direction: 'diagonal', angle: 45 }
    },
    {
      id: 'circle',
      name: 'Cercle',
      type: 'shape',
      data: { shape: 'circle', radius: 20 }
    },
    {
      id: 'square',
      name: 'Carré',
      type: 'shape',
      data: { shape: 'square', size: 40 }
    },
    {
      id: 'triangle',
      name: 'Triangle',
      type: 'shape',
      data: { shape: 'triangle', size: 40 }
    }
  ];

  const drawGrid = useCallback((ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 1;

    // Draw vertical lines
    for (let x = 0; x <= canvasWidth; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvasHeight);
      ctx.stroke();
    }

    // Draw horizontal lines
    for (let y = 0; y <= canvasHeight; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
    }

    // Reset stroke style
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = lineWidth;
  }, [canvasWidth, canvasHeight, selectedColor, lineWidth]);

  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Set default styles
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = lineWidth;

    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx);
    }
  }, [canvasWidth, canvasHeight, showGrid, selectedColor, lineWidth, drawGrid]);



  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx);
    }

    // Redraw all paths
    paths.forEach(path => {
      if (path.points.length < 2) return;

      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.width;
      ctx.beginPath();
      ctx.moveTo(path.points[0].x, path.points[0].y);

      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x, path.points[i].y);
      }

      ctx.stroke();
    });
  }, [paths, showGrid, drawGrid, canvasWidth, canvasHeight]);

  const getMousePos = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!allowInteraction) return;

    const pos = getMousePos(e);
    setIsDrawing(true);

    const newPath: DrawingPath = {
      id: `path_${Date.now()}`,
      points: [pos],
      color: selectedColor,
      width: lineWidth,
      tool: selectedTool
    };

    setCurrentPath(newPath);
    onAction?.('drawing_started', { position: pos, tool: selectedTool });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !currentPath) return;

    const pos = getMousePos(e);
    const updatedPath = { ...currentPath, points: [...currentPath.points, pos] };
    setCurrentPath(updatedPath);

    // Draw the line segment
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = selectedTool === 'eraser' ? '#FFFFFF' : selectedColor;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.moveTo(currentPath.points[currentPath.points.length - 1].x, currentPath.points[currentPath.points.length - 1].y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    onAction?.('drawing_continued', { position: pos, pathLength: updatedPath.points.length });
  };

  const handleMouseUp = () => {
    if (!currentPath) return;

    setIsDrawing(false);
    
    if (currentPath.points.length > 1) {
      setPaths(prev => [...prev, currentPath]);
      onAction?.('drawing_completed', { path: currentPath });
    }
    
    setCurrentPath(null);
  };

  const clearCanvas = () => {
    setPaths([]);
    onAction?.('canvas_cleared', {});
  };

  const toggleGrid = () => {
    setShowGrid(prev => !prev);
    onAction?.('grid_toggled', { showGrid: !showGrid });
  };

  const applyTemplate = (template: Template) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = lineWidth;

    if (template.type === 'line') {
      if (template.data.direction === 'horizontal') {
        for (let y = 20; y < canvasHeight; y += template.data.spacing * 40) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvasWidth, y);
          ctx.stroke();
        }
      } else if (template.data.direction === 'vertical') {
        for (let x = 20; x < canvasWidth; x += template.data.spacing * 40) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvasHeight);
          ctx.stroke();
        }
      }
    } else if (template.type === 'shape') {
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;

      if (template.data.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(centerX, centerY, template.data.radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (template.data.shape === 'square') {
        const size = template.data.size;
        ctx.strokeRect(centerX - size/2, centerY - size/2, size, size);
      } else if (template.data.shape === 'triangle') {
        const size = template.data.size;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - size/2);
        ctx.lineTo(centerX - size/2, centerY + size/2);
        ctx.lineTo(centerX + size/2, centerY + size/2);
        ctx.closePath();
        ctx.stroke();
      }
    }

    showFeedbackMessage('Template appliqué avec succès !', 'success');
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = 'drawing.png';
    link.href = canvas.toDataURL();
    link.click();
    onAction?.('canvas_downloaded', {});
  };

  const showFeedbackMessage = (message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    
    setTimeout(() => {
      setShowFeedback(false);
    }, 2000);
  };

  useEffect(() => {
    initializeCanvas();
  }, [initializeCanvas]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  return (
    <div 
      ref={setContainerRef}
      className="drawing-board-component w-full"
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {resource.name || 'Planche à Dessiner'}
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          {resource.description || 'Planche interactive pour exercices de tracé et quadrillage'}
        </p>
        {question && (
          <p className="text-md font-medium text-blue-600 mb-2">
            {question}
          </p>
        )}
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg">
        {/* Tools */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Outils:</span>
          {availableTools.includes('crayon') && (
            <button
              onClick={() => setSelectedTool('pen')}
              className={`p-2 rounded-lg ${selectedTool === 'pen' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              <PenTool className="w-4 h-4" />
            </button>
          )}
          {availableTools.includes('gomme') && (
            <button
              onClick={() => setSelectedTool('eraser')}
              className={`p-2 rounded-lg ${selectedTool === 'eraser' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              <Eraser className="w-4 h-4" />
            </button>
          )}
          {availableTools.includes('règle') && (
            <button
              onClick={() => setSelectedTool('ruler')}
              className={`p-2 rounded-lg ${selectedTool === 'ruler' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              <Ruler className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Colors */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Couleurs:</span>
          {colors.map((color) => (
            <button
              key={color}
              onClick={() => setSelectedColor(color)}
              className={`w-6 h-6 rounded-full border-2 ${selectedColor === color ? 'border-gray-800' : 'border-gray-300'}`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        {/* Line Width */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Épaisseur:</span>
          {lineWidths.map((width) => (
            <button
              key={width}
              onClick={() => setLineWidth(width)}
              className={`px-2 py-1 text-xs rounded ${lineWidth === width ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              {width}
            </button>
          ))}
        </div>

        {/* Grid Toggle */}
        <button
          onClick={toggleGrid}
          className={`p-2 rounded-lg ${showGrid ? 'bg-green-100 text-green-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
        >
          <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
            <div className="w-1 h-1 bg-current"></div>
            <div className="w-1 h-1 bg-current"></div>
            <div className="w-1 h-1 bg-current"></div>
            <div className="w-1 h-1 bg-current"></div>
          </div>
        </button>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={clearCanvas}
            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={downloadCanvas}
            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="mb-4">
        <canvas
          ref={canvasRef}
          className="border-2 border-gray-300 rounded-lg cursor-crosshair"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>

      {/* Templates */}
      {availableTemplates.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-900 mb-3">Templates:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => applyTemplate(template)}
                className="p-3 border border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 text-sm"
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feedback */}
      {showFeedback && (
        <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${
          feedbackType === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {feedbackType === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Instructions:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Sélectionnez un outil et une couleur</li>
          <li>• Cliquez et faites glisser pour dessiner</li>
          <li>• Utilisez la gomme pour effacer</li>
          <li>• Appliquez des templates pour des formes prédéfinies</li>
          <li>• Activez/désactivez la grille selon vos besoins</li>
          <li>• Téléchargez votre dessin quand vous avez terminé</li>
        </ul>
      </div>
    </div>
  );
};
